import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { DetailHeader } from '@/components/ui/detail';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { BRAND_DETAIL_TABS } from '@/Pages/tabRoutes';
import { useBrandQuery, useRemoveBrandMutation } from './brandsApi';
import { messageOf } from './lib';
import BrandFormDialog from './components/BrandFormDialog';
import BranchesPanel from './components/BranchesPanel';
import StandardsPanel from './components/StandardsPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import RoyaltyPanel from './components/RoyaltyPanel';
import OnboardingPanel from './components/OnboardingPanel';

/**
 * ADR-0023 §19 — one brand: its own branches, standards, consolidated
 * analytics, royalty schemes/statements and onboarding templates.
 *
 * `overview` and `standards` ride the route's own `brands.view` gate;
 * `analytics`, `royalty` and `onboarding` are each narrowed again to their
 * own permission by `BRAND_DETAIL_TABS` — a franchisee branch manager who
 * holds only `brands.view`/`royalty.view` sees Overview, Standards and
 * Royalty, never Analytics or Onboarding, matching `roles.ts`'s
 * `BRANCH_MANAGER` grant.
 */
export default function BrandDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const { brandId } = useParams<{ brandId: string }>();
  const routed = useRoutedTabs(`/brands/${brandId}`, BRAND_DETAIL_TABS);

  const [editOpen, setEditOpen] = useState(false);
  const query = useBrandQuery(brandId ?? '', { skip: !brandId });
  const [removeBrand, { isLoading: removing }] = useRemoveBrandMutation();

  const canManage = can('brands.manage');

  if (!brandId) return <Navigate to="/brands" replace />;
  if (query.isLoading) return <LoadingState />;
  if (query.error) {
    const status = (query.error as { status?: number } | null)?.status;
    // A 404 is "this brand does not exist" — permanent, no retry offered.
    // Anything else (network, 500, a scope refusal) is "the request failed",
    // which IS worth retrying — collapsing the two would either offer a
    // useless retry on a brand that was deleted, or tell someone a brand they
    // can actually reach does not exist.
    if (status === 404) {
      return <EmptyState title={t('brands.detail.notFound')} hint={t('brands.detail.notFoundHint')} />;
    }
    return (
      <ErrorState message={messageOf(query.error, t('brands.detail.loadFailed'))} onRetry={() => query.refetch()} />
    );
  }
  const brand = query.data;
  if (!brand) {
    return <EmptyState title={t('brands.detail.notFound')} hint={t('brands.detail.notFoundHint')} />;
  }

  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  const doRemove = async () => {
    const ok = await confirm({
      title: t('brands.detail.removeConfirmTitle', { name: brand.name }),
      body: t('brands.detail.removeConfirmBody'),
      confirmLabel: t('brands.detail.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await removeBrand(brand.id).unwrap();
      toast.success(t('brands.detail.removed'));
      navigate('/brands');
    } catch (e) {
      toast.error(messageOf(e, t('brands.detail.removeFailed')));
    }
  };

  return (
    <div className="space-y-5">
      <DetailHeader
        title={brand.name}
        subtitle={t('brands.detail.branchCount', { count: brand._count.branches })}
        actions={
          canManage ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" />
                {t('common.edit')}
              </Button>
              <Button variant="danger" size="sm" onClick={doRemove} disabled={removing}>
                <Trash2 className="h-4 w-4" />
                {t('brands.detail.remove')}
              </Button>
            </>
          ) : undefined
        }
      />

      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={brand.name}
        idPrefix="brand-detail"
      />

      {current === 'overview' && <BranchesPanel brand={brand} />}
      {current === 'standards' && <StandardsPanel brand={brand} />}
      {current === 'analytics' && <AnalyticsPanel brand={brand} />}
      {current === 'royalty' && <RoyaltyPanel brand={brand} />}
      {current === 'onboarding' && <OnboardingPanel brand={brand} />}

      {editOpen && <BrandFormDialog brand={brand} onClose={() => setEditOpen(false)} />}
    </div>
  );
}
